import type { ComponentType } from 'react';
import AboutManual from './sections/AboutManual';
import ModerationManual from './sections/ModerationManual';
import ReputationManual from './sections/ReputationManual';
import StoryAnalysisManual from './sections/StoryAnalysisManual';
import VouchingManual from './sections/VouchingManual';

export interface ManualSectionDef {
  slug: string;
  /** Page title + the label shown on the `/readme` index card. */
  title: string;
  /** One-line description for the index card. */
  summary: string;
  Component: ComponentType;
}

/**
 * slug → section: the single source of truth driving the `/readme` index,
 * the `/readme/:slug` route lookup, and the targets of the feature→slug map
 * (`constants/manual.constants.ts`). Adding a manual page is one entry here
 * plus its content component — no routing changes (TRIL-424).
 *
 * Content stories register their own section here:
 *   - `vouching`       → TRIL-422
 *   - `story-analysis` → TRIL-425
 */
export const MANUAL_REGISTRY = {
  vouching: {
    slug: 'vouching',
    title: 'Vouching',
    summary:
      'How a vouch boosts reach over 60 days, what revoking and “winding down” mean, and how it resolves (return / success / burn).',
    Component: VouchingManual,
  },
  'story-analysis': {
    slug: 'story-analysis',
    title: 'Story analysis',
    summary:
      'What the analysis is looking at, how to read each signal, and why we don’t publish the exact formula.',
    Component: StoryAnalysisManual,
  },
  reputation: {
    slug: 'reputation',
    title: 'Reputation',
    summary:
      'What your reputation reflects, the eight dimensions behind it, and how it moves — earned over time, not gamed.',
    Component: ReputationManual,
  },
  moderation: {
    slug: 'moderation',
    title: 'Reporting & moderation',
    summary:
      'What happens when you report a post, and what the outcomes mean if your own post is reported.',
    Component: ModerationManual,
  },
  about: {
    slug: 'about',
    title: 'About this manual',
    summary: 'What the manual is, who it is for, and how its deep-links work.',
    Component: AboutManual,
  },
} satisfies Record<string, ManualSectionDef>;

/**
 * The registered slugs, derived from the registry keys. `satisfies` (rather than
 * an explicit `Record<string, …>` annotation) preserves the literal keys so this
 * type is exact — letting the feature→slug map be checked at compile time.
 */
export type ManualSlug = keyof typeof MANUAL_REGISTRY;

/** Ordered list for the index page (the registry object itself is unordered). */
export const MANUAL_SECTIONS: ManualSectionDef[] =
  Object.values(MANUAL_REGISTRY);

export const getManualSection = (slug: string): ManualSectionDef | undefined =>
  (MANUAL_REGISTRY as Record<string, ManualSectionDef>)[slug];
